import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Two axios instances on purpose.
//
// PUBLIC (`api`): no credentials. Used for the audit funnel — analyze, poll,
//   draft, results fetch. This is critical: the Emergent ingress sends
//   `Access-Control-Allow-Origin: *` on every response, which the browser
//   refuses if the request is credentialed. Public endpoints don't need a
//   cookie anyway, so we don't ask for one and CORS is satisfied.
//
// AUTHED (`apiAuth`): withCredentials=true. Used only for the member
//   workspace (Google OAuth session cookie). These calls require the
//   backend to send a specific (non-wildcard) origin back, which our
//   FastAPI CORSMiddleware does correctly when not behind the wildcard
//   ingress — and on same-origin deployments (the preview env) the cookie
//   flows fine anyway.
export const api = axios.create({
  baseURL: API,
});

export const apiAuth = axios.create({
  baseURL: API,
  withCredentials: true,
});

/**
 * Retry a network call once on transient failure (network down, 502/503/504,
 * or no response at all). Validation errors (4xx) and budget errors (402)
 * are NOT retried — they won't recover.
 */
async function withRetry(fn, { delayMs = 1500 } = {}) {
  try {
    return await fn();
  } catch (e) {
    const status = e?.response?.status;
    const isTransient =
      !e?.response /* network failure */ ||
      status === 502 ||
      status === 503 ||
      status === 504;
    if (!isTransient) throw e;
    await new Promise((r) => setTimeout(r, delayMs));
    return fn();
  }
}

export async function analyzeAudit(payload) {
  const { data } = await withRetry(() => api.post("/audit/analyze", payload), { delayMs: 2500 });
  return data;
}

export async function getAudit(id) {
  // Called every 3s from the polling loop — don't retry, the next poll will retry naturally.
  const { data } = await api.get(`/audit/${id}`);
  return data;
}

export async function saveAudit(id) {
  // Requires the logged-in user's session cookie.
  const { data } = await withRetry(() => apiAuth.post(`/audit/${id}/save`));
  return data;
}

export async function deleteAudit(id, { email } = {}) {
  // Soft-delete. Two paths:
  // - public: pass `email` to prove ownership.
  // - member: pass nothing; the apiAuth session cookie proves it for the
  //   logged-in user (backend matches their Google email to the audit's email).
  const client = email ? api : apiAuth;
  const { data } = await client.delete(`/audit/${id}`, { data: { email: email || null } });
  return data;
}

export async function adminAuditsByEmail(email, { includeDeleted = false } = {}) {
  const { data } = await apiAuth.get("/admin/audits", {
    params: { email, include_deleted: includeDeleted },
  });
  return data;
}

export async function adminWhoami() {
  const { data } = await apiAuth.get("/admin/whoami");
  return data;
}

export async function listAudits() {
  const { data } = await apiAuth.get("/audit/list");
  return data;
}

export async function saveAuditDraft(payload) {
  const { data } = await withRetry(() => api.post("/audit/draft", payload));
  return data;
}

export async function getAuditDraft(email) {
  const { data } = await withRetry(() =>
    api.get("/audit/draft", { params: { email } })
  );
  return data;
}

export async function authMe() {
  const { data } = await apiAuth.get("/auth/me");
  return data;
}

export async function authSession(session_id) {
  const { data } = await apiAuth.post("/auth/session", { session_id });
  return data;
}

export async function authLogout() {
  const { data } = await apiAuth.post("/auth/logout");
  return data;
}
